// Copyright 2025. WebPros International GmbH. All rights reserved.

import { Translate } from '@plesk/ui-library';
import { Dialog } from 'common/components/Dialog/Dialog';
import { IComputeResourceRecoveryReadinessStatusResponse } from 'common/api/resources/DisasterRecovery';
import ErrorMessage from 'admin/disasterRecovery/components/ErrorMessage/ErrorMessage';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    compatibilityDetails: IComputeResourceRecoveryReadinessStatusResponse['destination_compute_resources'][number]['compatibility_details'] | null;
}

export default function OverallDialog({
    isOpen,
    onClose,
    compatibilityDetails,
}: IProps) {

    const kvmNotSupported =
        'kvm' in (compatibilityDetails?.supported_virtualization_types ?? {}) &&
        !compatibilityDetails?.supported_virtualization_types.kvm;

    const vzNotSupported =
        'vz' in (compatibilityDetails?.supported_virtualization_types ?? {}) &&
        !compatibilityDetails?.supported_virtualization_types.vz;

    return (
        <Dialog
            heading={<Translate content="disasterRecovery.readinessStatus.destinationComputeResources.table.overall" />}
            closeHandler={onClose}
            isOpen={isOpen}
            size={'sm'}
        >
            {!compatibilityDetails?.destination_cr_is_active && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.statusError" />
                </ErrorMessage>
            )}

            {compatibilityDetails?.compatible_destination_cr_ip_blocks_count !== compatibilityDetails?.source_cr_ip_blocks_count && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.ipBlocksError" />
                </ErrorMessage>
            )}

            {(kvmNotSupported || vzNotSupported) && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.virtualizationTypeError" />
                </ErrorMessage>
            )}

            {!!compatibilityDetails?.incompatible_storages.length && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.storagesError" />
                </ErrorMessage>
            )}

            {!compatibilityDetails?.arch_type_is_compatible && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.archTypeError" />
                </ErrorMessage>
            )}

            {(!compatibilityDetails?.sources_is_compatible || !compatibilityDetails?.products_is_compatible) && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.sourceError" />
                </ErrorMessage>
            )}

            {(!compatibilityDetails?.source_cr_capabilities_exists ||
                !compatibilityDetails?.source_cr_os_is_supported ||
                !compatibilityDetails?.destination_cr_capabilities_exists ||
                !compatibilityDetails?.destination_cr_os_is_supported) && (
                <ErrorMessage>
                    <Translate content="disasterRecovery.readinessStatus.destinationComputeResources.dialogs.overall.capabilitiesError" />
                </ErrorMessage>
            )}
        </Dialog>
    );
}